import React, { Component, createRef } from 'react';
import { createPortal } from 'react-dom';
import styles from './styles/Gallery.module.scss';

// ---- Types ----

export interface GalleryImage {
  src: string;
  alt: string;
  maxWidth: number;
  maxHeight: number;
  float?: 'left' | 'right';
}

interface GalleryProps {
  images: GalleryImage[];
  showCaptions?: boolean;
}

interface ColItem {
  img: GalleryImage;
  originalIdx: number;
}

interface GalleryState {
  columnItems: ColItem[][];
  loadedCount: number;
  lightboxIndex: number | null;
  zoomLevel: number;
  panX: number;
  panY: number;
  isDragging: boolean;
  dragStartX: number;
  dragStartY: number;
  dragStartPanX: number;
  dragStartPanY: number;
  maxZoom: number;  // 0 = noch nicht geladen; >1 = Zoom moeglich
}

const PAGE_SIZE = 8;
// Geschätzte Spaltenbreite für die Höhenberechnung (4-Spalten-Layout ~1280px Viewport)
const ESTIMATED_COL_WIDTH = 280;

// ---- Component ----

class Gallery extends Component<GalleryProps, GalleryState> {
  private sentinelRef = createRef<HTMLDivElement>();
  private columnRefs: React.RefObject<HTMLDivElement | null>[];
  private observer: IntersectionObserver | null = null;
  private parentResizeObserver: ResizeObserver | null = null;
  private floatWrapperRef = createRef<HTMLDivElement>();
  private lightboxImgRef = createRef<HTMLImageElement>();
  private readonly cols: number;

  constructor(props: GalleryProps) {
    super(props);
    this.cols = Math.min(4, Math.max(1, props.images.length));
    this.columnRefs = Array.from({ length: this.cols }, () => createRef<HTMLDivElement>());
    this.state = {
      columnItems: Array.from({ length: this.cols }, () => []),
      loadedCount: 0,
      lightboxIndex: null,
      zoomLevel: 1,
      panX: 0,
      panY: 0,
      isDragging: false,
      dragStartX: 0,
      dragStartY: 0,
      dragStartPanX: 0,
      dragStartPanY: 0,
      maxZoom: 0,
    };
  }

  componentDidMount(): void {
    this.loadMore();
    this.setupObserver();
    document.addEventListener('keydown', this.handleKeyDown);
    const { images } = this.props;
    if (images.length === 1 && images[0].float) {
      this.setupFloatCenterCheck();
    }
  }

  componentDidUpdate(_prevProps: GalleryProps, prevState: GalleryState): void {
    if (prevState.loadedCount !== this.state.loadedCount) {
      if (this.state.loadedCount >= this.props.images.length) {
        this.observer?.disconnect();
      }
    }
  }

  componentWillUnmount(): void {
    this.observer?.disconnect();
    this.parentResizeObserver?.disconnect();
    window.removeEventListener('resize', this.floatCenterCheck);
    window.removeEventListener('mousemove', this.handleMouseMove);
    window.removeEventListener('mouseup', this.handleMouseUp);
    document.removeEventListener('keydown', this.handleKeyDown);
  }

  private setupObserver(): void {
    if (!this.sentinelRef.current) return;
    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          this.loadMore();
        }
      },
      { rootMargin: '200px' }
    );
    this.observer.observe(this.sentinelRef.current);
  }

  private floatCenterCheck = (): void => {
    const inner = this.floatWrapperRef.current;
    const parent = inner?.parentElement;
    if (!inner || !parent) return;

    // gallery-center temporaer entfernen, damit inner.offsetWidth die natuerliche
    // Breite liefert (mit der Klasse wuerde der Container auf volle Seitenbreite aufgehen)
    parent.classList.remove('gallery-center');

    const contentWidth = inner.offsetWidth;
    if (contentWidth <= 0) return;

    const half = Math.min(window.innerWidth, window.outerWidth || window.innerWidth) / 2;
    if (contentWidth > half) {
      parent.classList.add('gallery-center');
    }
  };

  private setupFloatCenterCheck(): void {
    window.addEventListener('resize', this.floatCenterCheck, { passive: true });
    if (typeof ResizeObserver !== 'undefined') {
      // document.body beobachten (statt parent), damit Klassen-Aenderungen am parent
      // keine Endlosschleife ausloesen
      this.parentResizeObserver = new ResizeObserver(this.floatCenterCheck);
      this.parentResizeObserver.observe(document.body);
    }
    this.floatCenterCheck();
  }

  private loadMore = (): void => {
    const { images } = this.props;
    const { loadedCount, columnItems } = this.state;
    if (loadedCount >= images.length) return;

    const nextBatch = images.slice(loadedCount, Math.min(loadedCount + PAGE_SIZE, images.length));
    const newCols: ColItem[][] = columnItems.map(col => [...col]);

    // Echte Spaltenhöhen aus dem DOM lesen (beim ersten Laden: alle 0)
    const virtualHeights = this.columnRefs.map(ref => ref.current?.offsetHeight ?? 0);

    let absoluteIdx = loadedCount;
    for (const img of nextBatch) {
      // Kürzeste Spalte finden; bei Gleichstand: Spalte mit wenigsten Einträgen
      let minH = virtualHeights[0];
      let minCol = 0;
      for (let c = 1; c < this.cols; c++) {
        if (
          virtualHeights[c] < minH ||
          (virtualHeights[c] === minH && newCols[c].length < newCols[minCol].length)
        ) {
          minH = virtualHeights[c];
          minCol = c;
        }
      }
      newCols[minCol].push({ img, originalIdx: absoluteIdx });
      // Geschätzte Höhe anhand des echten Seitenverhältnisses addieren,
      // damit hohe Bilder nicht mehrfach in dieselbe Spalte landen
      const estimatedHeight =
        img.maxWidth > 0 && img.maxHeight > 0
          ? (ESTIMATED_COL_WIDTH / img.maxWidth) * img.maxHeight
          : ESTIMATED_COL_WIDTH; // quadratischer Fallback
      virtualHeights[minCol] += estimatedHeight;
      absoluteIdx++;
    }

    this.setState({
      columnItems: newCols,
      loadedCount: absoluteIdx,
    });
  };

  private openLightbox = (index: number): void => {
    this.setState({ lightboxIndex: index, zoomLevel: 1, panX: 0, panY: 0, maxZoom: 0 });
    document.body.style.overflow = 'hidden';
  };

  private closeLightbox = (): void => {
    this.setState({ lightboxIndex: null, zoomLevel: 1, panX: 0, panY: 0, isDragging: false, maxZoom: 0 });
    document.body.style.overflow = '';
    window.removeEventListener('mousemove', this.handleMouseMove);
    window.removeEventListener('mouseup', this.handleMouseUp);
  };

  private navigate = (delta: number): void => {
    const { images } = this.props;
    this.setState((prev) => {
      if (prev.lightboxIndex === null) return null;
      const next = prev.lightboxIndex + delta;
      if (next < 0 || next >= images.length) return null;
      return { lightboxIndex: next, zoomLevel: 1, panX: 0, panY: 0, maxZoom: 0 };
    });
  };

  private handleKeyDown = (e: KeyboardEvent): void => {
    if (this.state.lightboxIndex === null) return;
    if (e.key === 'ArrowRight') this.navigate(1);
    else if (e.key === 'ArrowLeft') this.navigate(-1);
    else if (e.key === 'Escape') this.closeLightbox();
    else if (e.key === '+' || e.key === '=') this.zoomIn();
    else if (e.key === '-') this.zoomOut();
    else if (e.key === '0') this.setState({ zoomLevel: 1, panX: 0, panY: 0 });
  };

  private handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>): void => {
    if (e.target === e.currentTarget) this.closeLightbox();
  };

  // ---- Zoom & Pan ----

  private zoomIn = (): void => {
    this.setState((prev) => ({
      zoomLevel: Math.min(+(prev.zoomLevel * 1.3).toFixed(4), prev.maxZoom > 0 ? prev.maxZoom : 4),
    }));
  };

  private zoomOut = (): void => {
    this.setState((prev) => {
      const next = Math.max(+(prev.zoomLevel / 1.3).toFixed(4), 1);
      return { zoomLevel: next, panX: next === 1 ? 0 : prev.panX, panY: next === 1 ? 0 : prev.panY };
    });
  };

  private handleWheel = (e: React.WheelEvent): void => {
    e.preventDefault();
    if (e.deltaY < 0) this.zoomIn();
    else this.zoomOut();
  };

  private handleImgLoad = (): void => {
    const img = this.lightboxImgRef.current;
    if (!img || img.offsetWidth <= 0 || img.naturalWidth <= 0) return;
    const ratio = img.naturalWidth / img.offsetWidth;
    // Nur Zoom anbieten wenn Bild groesser als gerendert (ratio > 1.05 Toleranz)
    this.setState({ maxZoom: ratio > 1.05 ? Math.min(ratio, 4) : 1 });
  };

  private handleMouseDown = (e: React.MouseEvent): void => {
    if (this.state.zoomLevel <= 1) return;
    e.preventDefault();
    const { panX, panY } = this.state;
    this.setState({
      isDragging: true,
      dragStartX: e.clientX,
      dragStartY: e.clientY,
      dragStartPanX: panX,
      dragStartPanY: panY,
    });
    window.addEventListener('mousemove', this.handleMouseMove);
    window.addEventListener('mouseup', this.handleMouseUp);
  };

  private handleMouseMove = (e: MouseEvent): void => {
    const { isDragging, dragStartX, dragStartY, dragStartPanX, dragStartPanY } = this.state;
    if (!isDragging) return;
    this.setState({
      panX: dragStartPanX + (e.clientX - dragStartX),
      panY: dragStartPanY + (e.clientY - dragStartY),
    });
  };

  private handleMouseUp = (): void => {
    this.setState({ isDragging: false });
    window.removeEventListener('mousemove', this.handleMouseMove);
    window.removeEventListener('mouseup', this.handleMouseUp);
  };

  private handleTouchStart = (e: React.TouchEvent): void => {
    if (this.state.zoomLevel <= 1 || e.touches.length !== 1) return;
    const t = e.touches[0];
    const { panX, panY } = this.state;
    this.setState({
      isDragging: true,
      dragStartX: t.clientX,
      dragStartY: t.clientY,
      dragStartPanX: panX,
      dragStartPanY: panY,
    });
  };

  private handleTouchMove = (e: React.TouchEvent): void => {
    const { isDragging, dragStartX, dragStartY, dragStartPanX, dragStartPanY } = this.state;
    if (!isDragging || e.touches.length !== 1) return;
    e.preventDefault();
    const t = e.touches[0];
    this.setState({
      panX: dragStartPanX + (t.clientX - dragStartX),
      panY: dragStartPanY + (t.clientY - dragStartY),
    });
  };

  private handleTouchEnd = (): void => {
    this.setState({ isDragging: false });
  };

  // ---- Render ----

  // Inline-Float: float+margin liegt bereits als inline-style auf dem React-Mountpoint
  // (gesetzt von PHP contentRenderPreviewGallery), daher kein weiteres float nötig.
  private renderInlineFloat(img: GalleryImage): React.ReactNode {
    const { showCaptions } = this.props;
    const imgStyle: React.CSSProperties = {
      display: 'block',
      width: img.maxWidth > 0 ? img.maxWidth : 'auto',
      maxWidth: '100%',
      maxHeight: img.maxHeight > 0 ? img.maxHeight : undefined,
      height: 'auto',
    };
    return (
      <>
        <div
          ref={this.floatWrapperRef}
          className={`${styles.galleryItem}${showCaptions ? ' ' + styles.galleryItemWithCaption : ''}`}
          onClick={() => this.openLightbox(0)}
          role="button"
          tabIndex={0}
          aria-label={img.alt || 'Bild öffnen'}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') this.openLightbox(0); }}
        >
          <img src={img.src} alt={img.alt} style={imgStyle} />
          {!showCaptions && img.alt && (
            <div className={styles.galleryItemOverlay}>
              <span className={styles.galleryItemOverlayLabel}>{img.alt.replaceAll("_"," ")}</span>
            </div>
          )}
          {showCaptions && img.alt && (
            <div className={styles.galleryCaption}>{img.alt.replaceAll("_"," ")}</div>
          )}
        </div>
        {this.renderLightbox()}
      </>
    );
  }

  private renderGrid(): React.ReactNode {
    const { columnItems } = this.state;
    const { showCaptions } = this.props;

    return (
      <div className={styles.galleryGrid}>
        {columnItems.map((colItems, colIdx) => (
          <div
            key={colIdx}
            className={styles.galleryColumn}
            ref={this.columnRefs[colIdx] as React.RefObject<HTMLDivElement>}
          >
            {colItems.map(({ img, originalIdx }) => (
              <div
                key={originalIdx}
                className={`${styles.galleryItem}${showCaptions ? ' ' + styles.galleryItemWithCaption : ''}`}
                onClick={() => this.openLightbox(originalIdx)}
                role="button"
                tabIndex={0}
                aria-label={img.alt || `Bild ${originalIdx + 1}`}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') this.openLightbox(originalIdx); }}
                style={{
                  width: img.maxWidth > 0 ? img.maxWidth : '100%',
                  maxWidth: '100%',
                }}
              >
                <img
                  className={styles.galleryItemImg}
                  src={img.src}
                  alt={img.alt}
                  loading="lazy"
                  style={{
                    width:     '100%',
                    maxHeight: img.maxHeight > 0 ? img.maxHeight : undefined,
                  }}
                />
                {!showCaptions && img.alt && (
                  <div className={styles.galleryItemOverlay}>
                    <span className={styles.galleryItemOverlayLabel}>{img.alt.replaceAll("_"," ")}</span>
                  </div>
                )}
                {showCaptions && img.alt && (
                  <div className={styles.galleryCaption}>{img.alt.replaceAll("_"," ")}</div>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  }

  private renderLightbox(): React.ReactNode {
    const { images } = this.props;
    const { lightboxIndex, zoomLevel, panX, panY, isDragging, maxZoom } = this.state;
    if (lightboxIndex === null) return null;

    const img = images[lightboxIndex];
    const hasPrev = lightboxIndex > 0;
    const hasNext = lightboxIndex < images.length - 1;
    const canZoom = maxZoom > 1;
    const isZoomed = zoomLevel > 1;

    const imgCursor = canZoom
      ? isDragging ? 'grabbing' : isZoomed ? 'grab' : 'zoom-in'
      : 'default';

    const imgTransform = isZoomed
      ? `translate(${panX}px, ${panY}px) scale(${zoomLevel})`
      : undefined;

    return createPortal(
      <div
        className={styles.lightboxBackdrop}
        onClick={this.handleBackdropClick}
        role="dialog"
        aria-modal="true"
        aria-label="Bildvorschau"
        onWheel={canZoom ? this.handleWheel : undefined}
      >
        <div className={styles.lightboxInner}>
          <div className={styles.lightboxZoomArea}>
            <img
              ref={this.lightboxImgRef}
              className={styles.lightboxImg}
              src={img.src}
              alt={img.alt.replaceAll("_"," ")}
              onLoad={this.handleImgLoad}
              onMouseDown={this.handleMouseDown}
              onTouchStart={this.handleTouchStart}
              onTouchMove={this.handleTouchMove}
              onTouchEnd={this.handleTouchEnd}
              draggable={false}
              style={{
                transform: imgTransform,
                cursor: imgCursor,
                transition: isDragging ? 'none' : 'transform 0.12s ease',
              }}
            />
          </div>

          <div className={styles.lightboxFooter}>
            {canZoom && (
              <div className={styles.lightboxZoomControls}>
                <button
                  className={styles.lightboxZoomBtn}
                  onClick={(e) => { e.stopPropagation(); this.zoomOut(); }}
                  disabled={!isZoomed}
                  aria-label="Herauszoomen"
                >−</button>
                <span className={styles.lightboxZoomLevel}>
                  {Math.round(zoomLevel * 100)}%
                </span>
                <button
                  className={styles.lightboxZoomBtn}
                  onClick={(e) => { e.stopPropagation(); this.zoomIn(); }}
                  disabled={zoomLevel >= maxZoom}
                  aria-label="Hineinzoomen"
                >+</button>
              </div>
            )}
            {img.alt && (
              <div className={styles.lightboxCaption}>{img.alt.replaceAll("_"," ")}</div>
            )}
          </div>
        </div>

        <button
          className={`${styles.lightboxNav} ${styles.lightboxNavPrev}`}
          onClick={() => this.navigate(-1)}
          disabled={!hasPrev}
          aria-label="Vorheriges Bild"
        >
          ‹
        </button>
        <button
          className={`${styles.lightboxNav} ${styles.lightboxNavNext}`}
          onClick={() => this.navigate(1)}
          disabled={!hasNext}
          aria-label="Nächstes Bild"
        >
          ›
        </button>
        <button
          className={styles.lightboxClose}
          onClick={this.closeLightbox}
          aria-label="Schließen"
        >
          ✕
        </button>
        {images.length > 1 && (
          <div className={styles.lightboxCounter}>
            {lightboxIndex + 1} / {images.length}
          </div>
        )}
      </div>,
      document.body
    );
  }

  render(): React.ReactNode {
    const { images } = this.props;
    const { loadedCount } = this.state;

    // Einzelbild mit Float: inline im Textfluss rendern statt als Masonry-Gallery
    if (images.length === 1 && images[0].float) {
      return this.renderInlineFloat(images[0]);
    }

    return (
      <div className={styles.galleryWrapper}>
        {this.renderGrid()}
        {loadedCount < images.length && (
          <div ref={this.sentinelRef} className={styles.loadSentinel} />
        )}
        {this.renderLightbox()}
      </div>
    );
  }
}

export default Gallery;

